import type { Map as MapLibreMap } from 'maplibre-gl'

import type { MapExtra } from '@/lib/map-layers'

/** The subset of MapLibre's `Map` API that `reconcileMapExtras` uses -- narrow on purpose so it's testable with a plain fake. */
export type ExtrasMap = Pick<
  MapLibreMap,
  | 'addSource'
  | 'addLayer'
  | 'removeLayer'
  | 'removeSource'
  | 'getLayer'
  | 'getSource'
  | 'setTerrain'
  | 'getTerrain'
>

/**
 * Brings the map's app-added extras (terrain DEM, hillshade, overlays, 3D
 * buildings -- see `buildMapExtras`) in line with `desired`, in place,
 * without reloading the basemap style:
 *
 * - removes previously-installed extras that are no longer desired
 *   (layer first, then its source if the app owns it),
 * - adds missing ones in bottom-to-top order, each inserted directly below
 *   the next desired extra (or below `anchorLayerId`, the app's lowest
 *   activity layer, so the route/markers always stay on top),
 * - sets or clears the terrain mesh.
 *
 * Idempotent: calling it twice with the same input is a no-op the second
 * time. After a full style reload (`setStyle`), pass `previous = []` --
 * the old style's extras are already gone.
 */
export function reconcileMapExtras(
  map: ExtrasMap,
  previous: readonly MapExtra[],
  desired: readonly MapExtra[],
  terrain: Parameters<MapLibreMap['setTerrain']>[0],
  anchorLayerId: string,
): void {
  const desiredLayerIds = new Set(desired.flatMap((e) => (e.layer != null ? [e.layer.id] : [])))
  const desiredOwnedSourceIds = new Set(
    desired.flatMap((e) => (e.source != null ? [e.sourceId] : [])),
  )

  // Terrain must be released before its DEM source can be removed.
  if (terrain == null && map.getTerrain() != null) map.setTerrain(null)

  for (const extra of previous) {
    if (extra.layer != null && !desiredLayerIds.has(extra.layer.id)) {
      if (map.getLayer(extra.layer.id) != null) map.removeLayer(extra.layer.id)
    }
  }
  for (const extra of previous) {
    if (extra.source != null && !desiredOwnedSourceIds.has(extra.sourceId)) {
      if (map.getSource(extra.sourceId) != null) map.removeSource(extra.sourceId)
    }
  }

  for (const extra of desired) {
    if (extra.source != null && map.getSource(extra.sourceId) == null) {
      map.addSource(extra.sourceId, extra.source)
    }
  }

  // Insert top-down so each missing layer can be placed directly beneath
  // the next desired layer that already exists.
  let beforeId: string | undefined = map.getLayer(anchorLayerId) != null ? anchorLayerId : undefined
  for (let i = desired.length - 1; i >= 0; i--) {
    const layer = desired[i]?.layer
    if (layer == null) continue
    if (map.getLayer(layer.id) == null) map.addLayer(layer, beforeId)
    beforeId = layer.id
  }

  if (terrain != null) map.setTerrain(terrain)
}
